import { useCallback, useEffect, useRef, useState } from 'react'
import { type IToast, toastStore } from 'shared/ui/Toast'
import { logger, writeTextToClipboard } from 'shared/lib'

const RESET_MS = 3000

type IUseCopyToClipboardParams = {
  showToast?: boolean
  mode?: 'stateful' | 'stateless'
  toastTitle?: string
  toastType?: IToast['type']
}

export const useCopyToClipboard = (
  value: string | number | undefined,
  {
    showToast = false,
    toastTitle = '',
    mode = 'stateful',
    toastType = 'info',
  }: IUseCopyToClipboardParams = {}
) => {
  const [copied, setCopied] = useState(false)
  const timerRef = useRef<number | null>(null)

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
    }
  }, [])

  const doCopy = useCallback(async (): Promise<boolean> => {
    if (!window?.navigator?.clipboard) {
      toastStore.add({ title: 'Clipboard not available', type: 'error' })
      return false
    }

    try {
      await writeTextToClipboard(`${value}`)
      if (showToast) {
        toastStore.add({
          title: toastTitle || 'Copied to clipboard',
          type: toastType,
        })
      }
      return true
    } catch (e) {
      logger.error(e)
      toastStore.add({ title: 'Failed to copy', type: 'error' })
      return false
    }
  }, [value, showToast, toastTitle, toastType])

  const clearCopied = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    setCopied(false)
  }, [])

  const onCopy = useCallback(async () => {
    if (mode === 'stateless') {
      return doCopy()
    }

    if (copied) return
    const ok = await doCopy()
    if (!ok) return

    setCopied(true)

    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = window.setTimeout(() => {
      setCopied(false)
      timerRef.current = null
    }, RESET_MS)
  }, [mode, copied, doCopy])

  return { onCopy, copied, clearCopied }
}
